/*
  Click and drag to scroll a horizontal row.

  A mouse has no horizontal wheel, so a row that scrolls sideways cannot be
  moved at all with one: trackpads can swipe, mice are stuck. This gives them
  a way, on any element marked data-drag-scroll.

  Touch is left alone. The browser's own scrolling is better than anything
  reproduced here, with momentum and rubber banding included.
*/
(() => {
  // Past this, a pointer drag is a drag rather than a click. Below it, small
  // movements while clicking should not count.
  const DRAG_THRESHOLD = 4;

  const attach = (row) => {
    if (row.dataset.dragReady) return;
    row.dataset.dragReady = 'true';

    let startX = 0;
    let startLeft = 0;
    let pressed = false;
    let dragging = false;
    let pointerId = null;

    row.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;

      pressed = true;
      dragging = false;
      pointerId = event.pointerId;
      startX = event.clientX;
      startLeft = row.scrollLeft;
    });

    row.addEventListener('pointermove', (event) => {
      if (!pressed) return;

      const delta = event.clientX - startX;

      /*
        Capture is taken here rather than on pointerdown, and only once the
        pointer has actually travelled. While a pointer is captured the
        browser retargets the click that follows to the capturing element,
        so capturing on every press made every click inside the row report
        the row as its target. Buttons within the cards then stopped
        working, because the handler looking for them never saw them.

        Taking it late means a plain click never captures at all, and a real
        drag still gets the capture it needs to keep following the pointer
        outside the row.
      */
      if (!dragging) {
        if (Math.abs(delta) <= DRAG_THRESHOLD) return;

        dragging = true;
        row.classList.add('is-dragging');
        row.setPointerCapture(pointerId);
      }

      row.scrollLeft = startLeft - delta;
    });

    const end = (event) => {
      if (!pressed) return;

      pressed = false;
      row.classList.remove('is-dragging');

      if (dragging && row.hasPointerCapture(event.pointerId)) {
        row.releasePointerCapture(event.pointerId);
      }
    };

    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);

    /*
      These rows are made of product cards, so unlike a row of plain images
      the drag ends on a link. Without this, letting go after dragging opens
      whichever product happened to be under the pointer.

      Only after a real drag: dragging is false for a plain click, so
      ordinary clicks pass straight through.
    */
    row.addEventListener(
      'click',
      (event) => {
        if (!dragging) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Without this the browser starts its own image drag and the row stops
    // following the pointer halfway through.
    row.addEventListener('dragstart', (event) => {
      if (dragging) event.preventDefault();
    });
  };

  /*
    Exposed, because rows can appear after this file has run: the quick add
    panel builds its gallery when it opens. Anything rendered later calls
    this rather than reimplementing the drag.
  */
  window.attachDragScroll = attach;

  const init = () => {
    document.querySelectorAll('[data-drag-scroll]').forEach(attach);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
